// Pure, client-safe helpers for masking recipient email addresses.
//
// The masked form is the ONLY representation an unpaid user is ever allowed to
// see. To keep the leak surface at zero, the server redacts contact emails to
// this masked form BEFORE serializing outreach card data to the client (see
// getPendingOutreach, /api/suggestions, /api/search/stream). The client-side
// mask (SwipeCard) is a defensive second layer only — with server masking in
// place there is no raw address in props/JSON/network for unpaid users.
//
// NO server-only imports here: this module is imported by both client
// components and server routes/actions.

import type { FeedItem } from '@/app/d/search/types';
import type { ContactEntityData } from '@/components/ui/design-system/entities/contact/ContactEntity.types';

/**
 * Mask a recipient address, e.g. `abhuds@goco.io` → `a•••@goco.io`.
 *
 * Idempotent: re-masking an already-masked address returns the same string
 * (the first local-part char and the full domain are preserved), so the server
 * can mask and the client can safely mask again without double-obscuring.
 */
export function maskRecipientEmail(email: string): string {
  const trimmed = email.trim();
  const at = trimmed.indexOf('@');
  if (at <= 0) return '•••';
  return `${trimmed[0]}•••@${trimmed.slice(at + 1)}`;
}

/** Mask a single contact's `email` + `emails` in place-free (returns a copy). */
function maskContactEmails(contact: ContactEntityData): ContactEntityData {
  if (!contact.email && !(contact.emails && contact.emails.length)) return contact;
  return {
    ...contact,
    email: contact.email ? maskRecipientEmail(contact.email) : contact.email,
    emails: contact.emails ? contact.emails.map(maskRecipientEmail) : contact.emails,
  };
}

/** Mask every contact address inside one feed item (returns a redacted copy). */
function maskFeedItemContacts(item: FeedItem): FeedItem {
  switch (item.type) {
    case 'contactsSection':
      return { ...item, contacts: item.contacts.map(maskContactEmails) };
    case 'jobsSection':
      return {
        ...item,
        jobs: item.jobs.map((jwc) => ({ ...jwc, contacts: jwc.contacts.map(maskContactEmails) })),
      };
    case 'topMatchesSection':
      return {
        ...item,
        matches: item.matches.map((m) => ({ ...m, contacts: m.contacts.map(maskContactEmails) })),
      };
    default:
      return item;
  }
}

/**
 * Redact every recipient email inside a feed for the client. Used server-side
 * for unpaid users so no raw address is ever serialized. Paid users skip this
 * entirely and receive the real addresses.
 */
export function maskFeedContactEmails(items: FeedItem[]): FeedItem[] {
  return items.map(maskFeedItemContacts);
}
